/**
 * @module Card
 *
 * Container component for grouping related content with consistent styling.
 * Provides structured sections for headers, content, and footers.
 *
 * @see {@link https://ui.shadcn.com/docs/components/card Shadcn Card}
 *
 * @example
 * // Basic card
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Subscription</CardTitle>
 *     <CardDescription>Your current plan details</CardDescription>
 *   </CardHeader>
 *   <CardContent>
 *     <p>Premium Plan - $9.99/month</p>
 *   </CardContent>
 *   <CardFooter>
 *     <Button>Manage</Button>
 *   </CardFooter>
 * </Card>
 */
import type { Ref } from "react";
import { cn } from "../lib/utils";

/** Main card container with border, shadow, and rounded corners. */
interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
}
const Card = ({
  className,
  ref,
  ...props
}: CardProps) => <div className={cn("rounded-lg border border-gray-150 border-solid bg-white text-gray-950 shadow-card dark:border-gray-800 dark:bg-gray-875 dark:text-gray-50", className)} ref={ref} {...props} data-sentry-component="Card" data-sentry-source-file="card.tsx" />;

/** Card header section with vertical spacing for title and description. */
interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
}
const CardHeader = ({
  className,
  ref,
  ...props
}: CardHeaderProps) => <div className={cn("flex flex-col space-y-1.5 p-6", className)} ref={ref} {...props} data-sentry-component="CardHeader" data-sentry-source-file="card.tsx" />;

/** Card title with semibold weight and larger text. */
interface CardTitleProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
}
const CardTitle = ({
  className,
  ref,
  ...props
}: CardTitleProps) => <div className={cn("font-semibold text-2xl leading-none tracking-tight", className)} ref={ref} {...props} data-sentry-component="CardTitle" data-sentry-source-file="card.tsx" />;

/** Muted description text below the card title. */
interface CardDescriptionProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
}
const CardDescription = ({
  className,
  ref,
  ...props
}: CardDescriptionProps) => <div className={cn("text-gray-700 text-sm dark:text-gray-400", className)} ref={ref} {...props} data-sentry-component="CardDescription" data-sentry-source-file="card.tsx" />;

/** Main content area of the card. */
interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
}
const CardContent = ({
  className,
  ref,
  ...props
}: CardContentProps) => <div className={cn("p-6 pt-0", className)} ref={ref} {...props} data-sentry-component="CardContent" data-sentry-source-file="card.tsx" />;

/** Card footer section, typically for actions/buttons. */
interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
}
const CardFooter = ({
  className,
  ref,
  ...props
}: CardFooterProps) => <div className={cn("flex items-center p-6 pt-0", className)} ref={ref} {...props} data-sentry-component="CardFooter" data-sentry-source-file="card.tsx" />;
export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };